/**
 * Account Setup Wizard - First Login
 * Step 1: Neues Passwort setzen
 * Step 2: 4-stelliger Zeiterfassungs-PIN festlegen
 */
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import { PasswordStep, PinStep, StepIndicator } from './account-setup/steps';
import { getToken, getUser, updateUser as persistUser } from '../lib/authStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const validatePassword = (newPassword, confirmPassword) => {
  const hasMinLength = newPassword.length >= 8;
  const hasUppercase = /[A-ZÄÖÜ]/.test(newPassword);
  const hasLowercase = /[a-zäöüß]/.test(newPassword);
  const hasNumber = /\d/.test(newPassword);
  const hasSpecial = /[^A-Za-z0-9äöüÄÖÜß]/.test(newPassword);
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;
  const valid = hasMinLength && hasUppercase && hasLowercase && hasNumber && hasSpecial && passwordsMatch;
  return { hasMinLength, hasUppercase, hasLowercase, hasNumber, hasSpecial, passwordsMatch, valid };
};

const submitSetup = async (newPassword, pin) => {
  const token = getToken();
  const res = await fetch(`${API_URL}/api/auth/setup-account`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ new_password: newPassword, pin }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.detail || 'Fehler bei der Einrichtung');
  }
  return res.json();
};

export default function AccountSetup() {
  const { setUser } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');

  const rules = validatePassword(newPassword, confirmPassword);
  const pinValid = pin.length === 4 && /^\d{4}$/.test(pin);
  const pinsMatch = pin === confirmPin && pin.length > 0;

  const handleNextStep = () => {
    if (!rules.valid) {
      toast.error('Bitte ein gueltiges Passwort eingeben');
      return;
    }
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!pinValid || !pinsMatch) {
      toast.error('Bitte einen gueltigen 4-stelligen PIN eingeben');
      return;
    }
    setSubmitting(true);
    try {
      await submitSetup(newPassword, pin);
      const savedUser = JSON.parse(getUser() || '{}');
      savedUser.must_change_password = false;
      persistUser(savedUser);
      setUser(savedUser);
      toast.success('Konto erfolgreich eingerichtet!');
    } catch (err) {
      toast.error(err.message || 'Fehler bei der Einrichtung');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/30 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center p-3 rounded-full bg-primary/10 mb-2">
            <ShieldCheck className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-heading font-bold text-2xl">Willkommen bei TeamHub!</h1>
          <p className="text-muted-foreground text-sm">
            Richten Sie Ihr Konto in zwei einfachen Schritten ein
          </p>
        </div>

        <StepIndicator step={step} />

        {step === 1 && (
          <PasswordStep
            newPassword={newPassword}
            setNewPassword={setNewPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            rules={rules}
            passwordValid={rules.valid}
            onNext={handleNextStep}
          />
        )}

        {step === 2 && (
          <PinStep
            pin={pin}
            setPin={setPin}
            confirmPin={confirmPin}
            setConfirmPin={setConfirmPin}
            pinValid={pinValid}
            pinsMatch={pinsMatch}
            submitting={submitting}
            onBack={() => setStep(1)}
            onSubmit={handleSubmit}
          />
        )}
      </div>
    </div>
  );
}
